// The admin's preview player. It plays, it reports where the playhead is, and
// it can hand back the current frame. It writes nothing.
//
// It used to own a second chapter editor and a third thumbnail uploader, both
// posting the whole record straight to the API with their errors swallowed --
// which is how the admin password ended up inside video records. Chapters now
// belong to ChapterManager and artwork to the Artwork tab; this component
// feeds both through `onPlayhead` and the imperative `captureFrame`.

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import { formatTime } from '../ps/format';

const PreviewPlayer = forwardRef(({ videoData, onPlayhead }, ref) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const canvasRef = useRef(null);
  const playheadRef = useRef(0);
  const onPlayheadRef = useRef(onPlayhead);
  const [mounted, setMounted] = useState(false);
  const [currentTime, setCurrentTime] = useState('00:00');
  const [duration, setDuration] = useState('00:00');
  const playerId = useRef(`videojs-player-${Math.random().toString(36).substring(2, 11)}`);

  const hlsUrl = videoData?.assets?.hls;
  const mp4Url = videoData?.assets?.mp4;
  const subtitles = videoData?.subtitles;
  const subtitlesRef = useRef(subtitles);
  subtitlesRef.current = subtitles;

  // Every store update produces a new record object, and therefore a new
  // `subtitles` array. Depending on that array's identity would dispose and
  // rebuild the player -- restarting playback -- on every save, which is
  // exactly what the mark-a-chapter-at-the-playhead workflow cannot survive.
  // Depend on the track sources instead.
  const subtitleKey = (subtitles || []).map((s) => `${s.srclang}:${s.src}`).join('|');

  // Kept in a ref so a changing callback identity cannot tear down the player.
  useEffect(() => {
    onPlayheadRef.current = onPlayhead;
  }, [onPlayhead]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useImperativeHandle(ref, () => ({
    // Seeks nowhere -- grabs the frame already on screen, so what you saw is
    // what you get. Returns null if the video element is not ready yet.
    captureFrame() {
      const videoElement = document.querySelector(`#${playerId.current} video.vjs-tech`);
      const canvas = canvasRef.current;
      if (!canvas || !videoElement || !videoElement.videoWidth) return null;

      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      canvas.getContext('2d').drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    },
    playhead() {
      return playheadRef.current;
    },
    seek(seconds) {
      if (playerRef.current) playerRef.current.currentTime(seconds);
    },
  }));

  useEffect(() => {
    if (!mounted) return undefined;

    const initializePlayer = () => {
      if (!videoRef.current) return;

      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }

      const player = videojs(videoRef.current, {
        controls: true,
        fluid: true,
        responsive: true,
        html5: {
          nativeTextTracks: false,
          vhs: {
            enableLowInitialPlaylist: false,
            smoothQualityChange: true,
            overrideNative: false,
            initialBandwidth: 6000000,
          },
        },
        sources: [
          { src: hlsUrl, type: 'application/x-mpegURL' },
          { src: mp4Url, type: 'video/mp4' },
        ],
      });

      player.on('timeupdate', () => {
        const ct = player.currentTime();
        playheadRef.current = ct;
        setCurrentTime(formatTime(ct));
        setDuration(formatTime(player.duration()));
        if (onPlayheadRef.current) onPlayheadRef.current(ct);
      });

      player.on('durationchange', () => setDuration(formatTime(player.duration())));

      (subtitlesRef.current || []).forEach((subtitle) => {
        player.addRemoteTextTrack(
          {
            kind: 'subtitles',
            srclang: subtitle.srclang,
            label: subtitle.label,
            src: subtitle.src,
            default: subtitle.default,
          },
          false,
        );
      });

      playerRef.current = player;
    };

    const timeoutId = setTimeout(initializePlayer, 100);

    return () => {
      clearTimeout(timeoutId);
      if (playerRef.current) {
        try {
          playerRef.current.dispose();
        } catch (e) {
          // The element is already gone; nothing to dispose.
        }
        playerRef.current = null;
      }
    };
  }, [hlsUrl, mp4Url, subtitleKey, mounted]);

  if (!mounted) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16/9',
          borderRadius: 'var(--ps-r-chip)',
          overflow: 'hidden',
          border: '1px solid var(--ps-line-subtle)',
          background: 'var(--ps-well)',
          '& .video-js': {
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundColor: 'transparent',
          },
          '& .vjs-tech': { position: 'absolute' },
          '& .vjs-big-play-button': {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          },
          '& .vjs-control-bar': { backgroundColor: 'rgba(0,0,0,0.7)' },
        }}
      >
        <div data-vjs-player>
          <video
            ref={videoRef}
            id={playerId.current}
            className="video-js vjs-big-play-centered"
            playsInline
            crossOrigin="anonymous"
          >
            <p className="vjs-no-js">
              To view this video please enable JavaScript, and consider upgrading to a web
              browser that supports HTML5 video
            </p>
          </video>
        </div>
      </Box>

      <Typography
        className="admin-mono"
        sx={{ mt: '6px', fontSize: 11.5, color: 'var(--ps-text-dim)', textAlign: 'center' }}
      >
        {currentTime} / {duration}
      </Typography>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Box>
  );
});

PreviewPlayer.displayName = 'PreviewPlayer';

export default PreviewPlayer;
